import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Chapter } from "@/components/Chapters";
import { Slider } from "@/components/Calculator";
import { scrollToSection } from "@/components/Navbar";
import { useLang } from "@/i18n";

const fmt = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

const reveal = {
  hidden: { opacity: 0, y: 48 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

export default function NetSheet() {
  const { t } = useLang();
  const [price, setPrice] = useState(550000);
  const [owe, setOwe] = useState(380000);
  const [commPct, setCommPct] = useState(5);
  const [closing, setClosing] = useState(6000);
  const [title, setTitle] = useState(1800);
  const [repairs, setRepairs] = useState(5000);

  const calc = useMemo(() => {
    const commission = (price * commPct) / 100;
    const net = price - owe - commission - closing - title - repairs;
    return { commission, net };
  }, [price, owe, commPct, closing, title, repairs]);

  const getExact = () => {
    const detail = {
      interest: "Selling a home",
      message: t.net.prefill
        .replace("{price}", fmt(price))
        .replace("{owe}", fmt(owe))
        .replace("{comm}", String(commPct))
        .replace("{closing}", fmt(closing))
        .replace("{title}", fmt(title))
        .replace("{repairs}", fmt(repairs))
        .replace("{net}", fmt(calc.net)),
    };
    window.dispatchEvent(new CustomEvent("opeam:prefill", { detail }));
    scrollToSection("#contact");
  };

  const rows = [
    { label: t.net.rowPrice, value: fmt(price), sign: "+", testid: "net-row-price" },
    { label: t.net.rowOwe, value: fmt(owe), sign: "−", testid: "net-row-owe" },
    { label: t.net.rowComm, value: fmt(calc.commission), sign: "−", testid: "net-row-comm" },
    { label: t.net.rowClosing, value: fmt(closing), sign: "−", testid: "net-row-closing" },
    { label: t.net.rowTitle, value: fmt(title), sign: "−", testid: "net-row-title" },
    { label: t.net.rowRepairs, value: fmt(repairs), sign: "−", testid: "net-row-repairs" },
  ];

  return (
    <Chapter id="net" number="08" eyebrow={t.net.eyebrow} testid="chapter-net">
      <motion.h2
        variants={reveal}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-15%" }}
        className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-2xl"
      >
        {t.net.h2a} <span className="italic text-copper">{t.net.h2b}</span>
      </motion.h2>
      <motion.p
        variants={reveal}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-15%" }}
        className="mt-8 max-w-xl text-base leading-relaxed text-bone/60"
      >
        {t.net.intro}
      </motion.p>

      <motion.div
        variants={reveal}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-10%" }}
        className="mt-14 grid lg:grid-cols-2 gap-px bg-white/10 border border-white/10"
      >
        <div className="bg-ink p-10 sm:p-14 space-y-10">
          <Slider label={t.net.price} display={fmt(price)} min={100000} max={2000000} step={5000} value={price} onChange={setPrice} testid="net-price-slider" />
          <Slider label={t.net.owe} display={fmt(owe)} min={0} max={1500000} step={5000} value={owe} onChange={setOwe} testid="net-owe-slider" />
          <div>
            <Slider label={t.net.commission} display={`${commPct}% · ${fmt(calc.commission)}`} min={0} max={7} step={0.25} value={commPct} onChange={setCommPct} testid="net-commission-slider" />
            <div className="flex gap-2 mt-4">
              {[5, 5.5, 6].map((p) => (
                <button
                  key={p}
                  data-testid={`net-comm-preset-${String(p).replace(".", "-")}`}
                  onClick={() => setCommPct(p)}
                  className={`flex-1 py-2.5 text-xs border transition-colors duration-300 ${
                    commPct === p
                      ? "border-copper text-copper"
                      : "border-white/15 text-bone/50 hover:border-white/40 hover:text-bone"
                  }`}
                >
                  {p}%
                </button>
              ))}
            </div>
          </div>
          <Slider label={t.net.closing} display={fmt(closing)} min={0} max={25000} step={250} value={closing} onChange={setClosing} testid="net-closing-slider" />
          <Slider label={t.net.title} display={fmt(title)} min={0} max={5000} step={50} value={title} onChange={setTitle} testid="net-title-slider" />
          <Slider label={t.net.repairs} display={fmt(repairs)} min={0} max={50000} step={250} value={repairs} onChange={setRepairs} testid="net-repairs-slider" />
        </div>

        <div className="bg-ink p-10 sm:p-14 flex flex-col justify-between gap-12">
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-bone/40">
              {t.net.resultTitle}
            </p>
            <div className="mt-4 overflow-hidden">
              <motion.span
                key={Math.round(calc.net)}
                data-testid="net-result"
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, ease: "easeOut" }}
                className={`block font-display text-6xl sm:text-7xl tracking-tight ${calc.net >= 0 ? "text-copper" : "text-red-400"}`}
              >
                {calc.net < 0 ? "−" : ""}{fmt(Math.abs(calc.net))}
              </motion.span>
            </div>
            <p className="mt-3 text-xs text-bone/35 leading-relaxed">{t.net.disclaimer}</p>
          </div>

          <div className="divide-y divide-white/10 border-y border-white/10 text-sm">
            {rows.map((r) => (
              <div key={r.testid} data-testid={r.testid} className="flex justify-between py-3">
                <span className="text-bone/40">{r.label}</span>
                <span className={r.sign === "−" ? "text-bone/60" : "text-bone/90"}>
                  {r.sign} {r.value}
                </span>
              </div>
            ))}
          </div>

          <button
            data-testid="net-cta-button"
            onClick={getExact}
            className="btn-swipe group flex items-center justify-center gap-3 border border-copper text-copper px-8 py-4 text-xs tracking-[0.25em] uppercase transition-colors duration-300 w-full"
          >
            {t.net.cta}
            <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </div>
      </motion.div>
    </Chapter>
  );
}
